import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import CdcrModal from '../cdcr/CdcrModal';
import OrderHistoryIconButton from '../orderHistory/OrderHistoryIconButton';
import BomAvailabilityPill, { availabilityReportForLine } from './BomAvailabilityPill';
import './OrderLineView.css';

const formatQty = (value) => {
    if (value == null || value === '') return '0';
    const num = Number(value);
    if (!Number.isFinite(num)) return String(value);
    return String(Math.trunc(num));
};

const formatPartNumber = (value) => {
    const partNumber = String(value || '');
    if (!partNumber) return 'N/A';
    return partNumber.length >= 3 ? partNumber.substring(3) : partNumber;
};

const placeMenu = (rect) => {
    const menuMax = 220;
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    const openAbove = spaceBelow < 140 && rect.top > spaceBelow;
    const width = Math.max(rect.width, 180);
    let left = rect.left;
    if (left + width > window.innerWidth - 8) left = Math.max(8, window.innerWidth - width - 8);
    return {
        top: openAbove ? Math.max(8, rect.top - menuMax) : rect.bottom + 2,
        left,
        width,
        maxHeight: menuMax,
    };
};

const OrderLineView = ({
    lines = [],
    detailsPending = false,
    getRowProps,
    renderComments,
    onHistory,
    getHistoryUnread,
    getOrderLabel,
    getFolderLink,
    renderShippingMethod,
    renderStickyNotes,
    onOrderAccess,
    onDeleteOrderAccess,
    onSelectOrderStatus,
    orderStatusSelections = {},
    submittedOrderStatuses = {},
    tagsToDeselect = {},
    hasOrderStatusChanges,
    onDeselectOrderTag,
    onSubmitOrderStatus,
    renderOrderStatusPresence,
    lineStatusSelections = {},
    submittedLineStatuses = {},
    lineTagsToDeselect = {},
    lineStatusDropdownOpen = {},
    onToggleLineStatusDropdown,
    onToggleLineStatus,
    onDeselectLineTag,
    hasLineStatusChanges,
    onSubmitLineStatus,
    lineStatusLockOwner,
    renderLinePresence,
    lineStatusTags = [],
    excludedPartsByOrder = {},
    onArchiveBomPart,
    onToggleExcludedAvailabilityPart,
}) => {
    const [bomOpen, setBomOpen] = useState({});
    const [bomSort, setBomSort] = useState({});
    const [pendingExcludedByOrder, setPendingExcludedByOrder] = useState({});
    const [partLinkMenu, setPartLinkMenu] = useState(null);
    const [lineStatusPos, setLineStatusPos] = useState({});
    const [cdcrLine, setCdcrLine] = useState(null);
    const lineStatusRefs = useRef({});

    useEffect(() => {
        setPendingExcludedByOrder(excludedPartsByOrder || {});
    }, [excludedPartsByOrder]);

    useEffect(() => {
        const openIds = Object.keys(lineStatusDropdownOpen || {}).filter((id) => lineStatusDropdownOpen[id]);
        if (!openIds.length) {
            setLineStatusPos({});
            return undefined;
        }
        const measure = () => {
            const next = {};
            openIds.forEach((id) => {
                const node = lineStatusRefs.current[id];
                if (node) next[id] = placeMenu(node.getBoundingClientRect());
            });
            setLineStatusPos(next);
        };
        const frame = requestAnimationFrame(measure);
        window.addEventListener('resize', measure);
        return () => {
            cancelAnimationFrame(frame);
            window.removeEventListener('resize', measure);
        };
    }, [lineStatusDropdownOpen]);

    useEffect(() => {
        if (!partLinkMenu) return undefined;
        const close = (event) => {
            const target = event.target;
            if (target instanceof Element && target.closest('.part-link-dropdown-menu, .order-line-part-link-btn')) return;
            setPartLinkMenu(null);
        };
        const closeOnScroll = (event) => {
            const target = event.target;
            if (target instanceof Element && target.closest('.part-link-dropdown-menu')) return;
            setPartLinkMenu(null);
        };
        document.addEventListener('mousedown', close);
        window.addEventListener('scroll', closeOnScroll, true);
        return () => {
            document.removeEventListener('mousedown', close);
            window.removeEventListener('scroll', closeOnScroll, true);
        };
    }, [partLinkMenu]);

    const toggleBom = (lineId) => {
        setBomOpen((prev) => ({ ...prev, [lineId]: !prev[lineId] }));
    };

    const openPartLinks = (event, key, partId) => {
        event.stopPropagation();
        if (!partId) return;
        if (partLinkMenu?.key === key) {
            setPartLinkMenu(null);
            return;
        }
        const rect = event.currentTarget.getBoundingClientRect();
        const menuHeight = 132;
        const menuWidth = 190;
        const spaceBelow = window.innerHeight - rect.bottom;
        const openAbove = spaceBelow < menuHeight && rect.top > spaceBelow;
        let left = rect.left;
        if (left + menuWidth > window.innerWidth - 8) left = Math.max(8, window.innerWidth - menuWidth - 8);
        setPartLinkMenu({
            key,
            partId,
            top: openAbove ? Math.max(8, rect.top - menuHeight) : rect.bottom + 2,
            left,
        });
    };

    const renderPartLinkButton = (key, partId) => {
        if (!partId) return null;
        return (
            <button
                type="button"
                className="btn btn-sm order-line-part-link-btn"
                title="View Part Details"
                aria-expanded={partLinkMenu?.key === key}
                onClick={(event) => openPartLinks(event, key, partId)}
            >
                <i className="fa fa-link" aria-hidden="true"></i>
            </button>
        );
    };

    const renderOrderStatus = (statusLine) => {
        const statusId = statusLine?.id;
        if (statusId == null) return null;
        const current = orderStatusSelections[statusId] || orderStatusSelections[String(statusId)] || [];
        const submitted = submittedOrderStatuses[statusId] || submittedOrderStatuses[String(statusId)] || [];
        const removing = tagsToDeselect[statusId] || tagsToDeselect[String(statusId)] || [];
        return (
            <div className="d-flex align-items-center gap-2">
                {renderOrderStatusPresence?.(statusId)}
                <div
                    className="form-control order-status-trigger d-flex align-items-center justify-content-between flex-grow-1"
                    style={{ cursor: 'pointer', minHeight: '38px', backgroundColor: 'white', border: '1px solid #ced4da' }}
                    onClick={(event) => onSelectOrderStatus?.(event, statusLine)}
                >
                    <div className="d-flex flex-wrap gap-1">
                        {current.map((tag, index) => (
                            <span
                                key={`current-${tag.label}-${index}`}
                                className="badge"
                                style={{ backgroundColor: tag.color, color: 'white', fontSize: '0.75rem', padding: '4px 8px', borderRadius: '12px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                            >
                                {tag.icon && <i className={tag.icon} aria-hidden="true"></i>}
                                {tag.label}
                            </span>
                        ))}
                        {submitted.map((tag, index) => {
                            const marked = removing.some((item) => item.label === tag.label);
                            return (
                                <span
                                    key={`submitted-${tag.label}-${index}`}
                                    className="badge"
                                    style={{
                                        backgroundColor: marked ? '#6c757d' : tag.color,
                                        color: 'white',
                                        fontSize: '0.75rem',
                                        padding: '4px 8px',
                                        borderRadius: '12px',
                                        cursor: 'pointer',
                                        opacity: marked ? 0.6 : 1,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                    }}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        onDeselectOrderTag?.(statusId, tag);
                                    }}
                                    title={marked ? 'Click to keep this tag' : 'Click to deselect this tag'}
                                >
                                    {tag.icon && <i className={tag.icon} aria-hidden="true"></i>}
                                    {tag.label}
                                </span>
                            );
                        })}
                        {current.length === 0 && submitted.length === 0 && (
                            <span className="text-muted">Select tags...</span>
                        )}
                    </div>
                </div>
                {hasOrderStatusChanges?.(statusId) && (
                    <button
                        type="button"
                        className="btn btn-success btn-sm order-status-submit-btn"
                        style={{ minWidth: '20px', height: '20px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', borderRadius: '2px' }}
                        onMouseDown={(event) => event.stopPropagation()}
                        onClick={(event) => {
                            event.stopPropagation();
                            onSubmitOrderStatus?.(statusId);
                        }}
                        title="Submit changes"
                    >
                        ✓
                    </button>
                )}
            </div>
        );
    };

    const renderLineStatus = (line) => {
        const itemId = String(line.id);
        const current = lineStatusSelections[itemId] || [];
        const submitted = submittedLineStatuses[itemId] || submittedLineStatuses[String(line.id)] || [];
        const removing = lineTagsToDeselect[itemId] || [];
        const lockedBy = lineStatusLockOwner?.(itemId);
        const position = lineStatusPos[itemId];
        const menuOpen = !!lineStatusDropdownOpen?.[itemId] && position;
        return (
            <div className="d-flex align-items-center gap-2 justify-content-center">
                <div className="line-status-dropdown position-relative flex-grow-1">
                    <div
                        ref={(node) => { lineStatusRefs.current[itemId] = node; }}
                        className="form-control d-flex align-items-center justify-content-between"
                        style={{ cursor: 'pointer', minHeight: '38px', backgroundColor: 'white', border: '1px solid #ced4da', fontSize: '12px' }}
                        onClick={() => onToggleLineStatusDropdown?.(itemId)}
                    >
                        <div className="d-flex flex-wrap gap-1">
                            {current.map((tag, index) => (
                                <span key={`sel-${tag.label}-${index}`} className="badge" style={{ backgroundColor: tag.color, color: 'white', fontSize: '0.75rem', padding: '4px 8px', borderRadius: '12px' }}>
                                    {tag.label}
                                    <button
                                        type="button"
                                        style={{ background: 'none', border: 'none', color: 'white', marginLeft: 4, cursor: 'pointer' }}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            onToggleLineStatus?.(itemId, tag, line.order?.ordernum);
                                        }}
                                    >
                                        ×
                                    </button>
                                </span>
                            ))}
                            {submitted.map((tag, index) => {
                                const marked = removing.some((item) => item.label === tag.label);
                                return (
                                    <span
                                        key={`sub-${tag.label}-${index}`}
                                        className="badge"
                                        style={{ backgroundColor: marked ? '#9ca3af' : tag.color, color: 'white', fontSize: '0.75rem', padding: '4px 8px', borderRadius: '12px', cursor: 'pointer', opacity: marked ? 0.7 : 1 }}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            onDeselectLineTag?.(itemId, tag);
                                        }}
                                        title={marked ? 'Pending removal' : 'Click to mark for removal'}
                                    >
                                        {tag.label}
                                    </span>
                                );
                            })}
                            {current.length === 0 && submitted.length === 0 && (
                                <span className="text-muted">Select tags...</span>
                            )}
                        </div>
                    </div>
                </div>
                {renderLinePresence?.(itemId)}
                {hasLineStatusChanges?.(itemId) && (
                    <button
                        type="button"
                        className="btn btn-success btn-sm line-status-submit-btn"
                        style={{ minWidth: '18px', height: '18px', padding: 0, fontSize: '8px' }}
                        disabled={Boolean(lockedBy)}
                        title={lockedBy ? `${lockedBy.name} is editing this line status` : 'Submit line status changes'}
                        onClick={(event) => {
                            event.stopPropagation();
                            onSubmitLineStatus?.(itemId);
                        }}
                    >
                        ✓
                    </button>
                )}
                {menuOpen && createPortal(
                    <div
                        className="line-status-dropdown-menu"
                        style={{
                            position: 'fixed',
                            top: position.top,
                            left: position.left,
                            width: position.width,
                            maxHeight: position.maxHeight,
                            overflowY: 'auto',
                            zIndex: 9999,
                            background: 'white',
                            border: '1px solid #ced4da',
                            borderRadius: 4,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        }}
                        onClick={(event) => event.stopPropagation()}
                    >
                        {lineStatusTags.map((tag) => {
                            const marked = removing.some((item) => item.label === tag.label);
                            const selected = current.some((item) => item.label === tag.label)
                                || (submitted.some((item) => item.label === tag.label) && !marked);
                            return (
                                <div
                                    key={tag.label}
                                    className="p-2 d-flex align-items-center"
                                    style={{ cursor: 'pointer', background: selected ? '#f8f9fa' : 'white', borderBottom: '1px solid #f1f1f1', fontSize: '12px' }}
                                    onClick={() => onToggleLineStatus?.(itemId, tag, line.order?.ordernum)}
                                >
                                    <span style={{ width: 10, height: 10, borderRadius: 2, background: tag.color, display: 'inline-block', marginRight: 8 }}></span>
                                    <span>{tag.label}</span>
                                    {selected && <span className="ms-auto">✓</span>}
                                </div>
                            );
                        })}
                    </div>,
                    document.body
                )}
            </div>
        );
    };

    const renderBom = (line) => {
        const bomParts = line?.bom?.bomParts || [];
        if (!bomParts.length) {
            return <p className="text-muted mb-0">No BOM parts available</p>;
        }
        const sortKey = String(line.id);
        const orderNum = line.order?.ordernum || line.ordernum;
        const orderKey = String(orderNum || '');
        const savedExcluded = excludedPartsByOrder?.[orderKey] || excludedPartsByOrder?.[orderNum] || {};
        const pendingExcluded = pendingExcludedByOrder?.[orderKey] || {};
        const sort = bomSort[sortKey] || { column: 'qtyAvail', direction: 'ascending' };
        const toggleSort = (column) => {
            setBomSort((prev) => {
                const current = prev[sortKey] || { column: 'qtyAvail', direction: 'ascending' };
                const sameColumn = current.column === column;
                return {
                    ...prev,
                    [sortKey]: {
                        column,
                        direction: sameColumn && current.direction === 'ascending' ? 'descending' : 'ascending',
                    },
                };
            });
        };
        const sortIcon = (column) => (sort.column === column ? (sort.direction === 'ascending' ? ' ▲' : ' ▼') : '');
        const rows = bomParts.map((bom, index) => {
            const part = bom?.part || {};
            const qoh = Number(part.qoh_cache) || 0;
            const qoo = Number(part.qoo_cache) || 0;
            const qr = Number(part.qr_cache) || 0;
            return {
                bom,
                index,
                partId: part.id,
                partLabel: formatPartNumber(bom?.prcpart),
                qtyTop: Number(bom?.qty_per_top) || 0,
                qoh,
                qoo,
                qr,
                qtyAvail: qoh - qr,
            };
        }).sort((a, b) => {
            const asc = sort.direction === 'ascending';
            if (sort.column === 'part') {
                return asc ? a.partLabel.localeCompare(b.partLabel) : b.partLabel.localeCompare(a.partLabel);
            }
            const valA = Number(a[sort.column]) || 0;
            const valB = Number(b[sort.column]) || 0;
            return asc ? valA - valB : valB - valA;
        });
        const header = (column, label) => (
            <th style={{ cursor: 'pointer' }} onClick={() => toggleSort(column)}>{label}{sortIcon(column)}</th>
        );
        return (
            <table className="table table-sm table-bordered mb-0 order-line-bom-table">
                <thead>
                    <tr className="text-center">
                        <th>S.No</th>
                        {header('part', 'Part')}
                        {header('qtyTop', 'Qty/Top')}
                        {header('qoh', 'QOH')}
                        {header('qoo', 'QOO')}
                        {header('qr', 'QR')}
                        {header('qtyAvail', 'Qty Avail')}
                        <th>Exclude</th>
                        <th>Archive</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, index) => {
                        const partNumber = row.bom?.prcpart;
                        const isArchived = !!row.bom?.archived;
                        const pendingOn = !!pendingExcluded[partNumber];
                        const savedOn = !!savedExcluded[partNumber];
                        return (
                        <tr
                            key={`${partNumber || 'bom'}-${row.index}`}
                            className={`text-center${isArchived ? ' is-archived' : ''}`}
                        >
                            <td>{index + 1}</td>
                            <td>
                                <div className="d-inline-flex align-items-center justify-content-center gap-1">
                                    <span>{row.partLabel}</span>
                                    {renderPartLinkButton(`bom-${sortKey}-${row.index}`, row.partId)}
                                </div>
                            </td>
                            <td>{formatQty(row.qtyTop)}</td>
                            <td>{row.qoh}</td>
                            <td>
                                <div className="d-inline-flex align-items-center justify-content-center">
                                    <span>{row.qoo}</span>
                                    {row.qoo > 0 && row.partId && (
                                        <a className="ms-2" href={`https://gigaflightinc.cetecerp.com/part/${row.partId}/pos`} target="_blank" rel="noreferrer" title="Purchase orders">
                                            <i className="fa fa-shopping-cart text-primary" aria-hidden="true"></i>
                                        </a>
                                    )}
                                </div>
                            </td>
                            <td>{row.qr}</td>
                            <td>
                                <span className={row.qtyAvail < 0 ? 'bg-danger text-white px-2 py-1 rounded' : ''}>{row.qtyAvail}</span>
                            </td>
                            <td>
                                {row.qtyAvail < 0 && partNumber ? (
                                    <div className="d-inline-flex align-items-center justify-content-center" style={{ gap: 8 }}>
                                        <button
                                            type="button"
                                            className={`order-line-bom-exclude-dot${pendingOn ? ' is-on' : ''}`}
                                            title="Select/deselect exclusion"
                                            aria-pressed={pendingOn}
                                            onClick={() => {
                                                setPendingExcludedByOrder((prev) => {
                                                    const current = prev[orderKey] || {};
                                                    return {
                                                        ...prev,
                                                        [orderKey]: { ...current, [partNumber]: !current[partNumber] },
                                                    };
                                                });
                                            }}
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-outline-success btn-sm order-line-bom-save"
                                            title="Save exclusion"
                                            disabled={!onToggleExcludedAvailabilityPart || savedOn === pendingOn}
                                            onClick={() => onToggleExcludedAvailabilityPart?.(orderKey, partNumber, pendingOn)}
                                        >
                                            Save
                                        </button>
                                    </div>
                                ) : (
                                    <span className="text-muted">-</span>
                                )}
                            </td>
                            <td>
                                <button
                                    type="button"
                                    className={`btn btn-sm order-line-bom-archive ${isArchived ? 'is-archived' : ''}`}
                                    title={isArchived ? 'Unarchive this BOM part' : 'Archive this BOM part'}
                                    disabled={!onArchiveBomPart || !row.bom?.id}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        onArchiveBomPart?.(row.bom.id, !isArchived, orderNum);
                                    }}
                                >
                                    {isArchived ? 'Unarchive' : 'Archive'}
                                </button>
                            </td>
                        </tr>
                        );
                    })}
                </tbody>
            </table>
        );
    };

    const renderOrderAccess = (line) => {
        const accessLine = line._headerLine || line;
        const urls = accessLine?.order?.orderUrls || [];
        return (
            <div className="d-flex flex-column align-items-center justify-content-center gap-2" style={{ width: '100%', maxWidth: '140px', margin: '0 auto' }}>
                {urls.length > 0 && (
                    <div className="d-flex flex-wrap gap-1 justify-content-center" style={{ width: '100%' }}>
                        {urls.map((urlItem) => (
                            <div
                                key={urlItem.id}
                                style={{ display: 'inline-flex', alignItems: 'center', backgroundColor: '#f0f0f0', border: '1px solid #ddd', borderRadius: '12px', padding: '3px 6px', fontSize: '10px', maxWidth: '130px' }}
                            >
                                <a
                                    href={urlItem.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ textDecoration: 'none', color: '#007bff', fontWeight: 500, marginRight: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                    title={`${urlItem.urlName}: ${urlItem.url}`}
                                >
                                    {urlItem.urlName}
                                </a>
                                <button
                                    type="button"
                                    className="order-access-remove"
                                    onClick={() => onDeleteOrderAccess?.(accessLine.id, urlItem.id, accessLine.order?.ordernum, urlItem.urlName)}
                                    title="Delete this URL"
                                >
                                    ×
                                </button>
                            </div>
                        ))}
                    </div>
                )}
                <button
                    type="button"
                    className="btn btn-outline-secondary"
                    style={{ fontSize: '0.7rem', padding: '3px 8px', height: '28px' }}
                    onClick={() => onOrderAccess?.(accessLine)}
                    title="Add folder access link"
                >
                    <i className="fa fa-plus" style={{ marginRight: 3 }}></i>
                    Add
                </button>
            </div>
        );
    };

    if (!lines.length) {
        return <div className="text-center text-muted py-4">No order lines available</div>;
    }

    return (
        <div className="order-line-view">
            {detailsPending && (
                <div className="order-line-view-note">Loading every line for these orders…</div>
            )}
            <table className="table gf-table table-bordered table-hover mb-0 orders-table order-line-view-table">
                <thead className="sticky-top">
                    <tr>
                        <th className="text-center" style={{ minWidth: 130 }}>Info</th>
                        <th className="text-center" style={{ minWidth: 70 }}>Line</th>
                        <th className="text-center" style={{ minWidth: 170 }}>Order #</th>
                        <th className="text-center" style={{ minWidth: 180 }}>Customer Name</th>
                        <th className="text-center" style={{ minWidth: 120 }}>WO #</th>
                        <th className="text-center" style={{ minWidth: 120 }}>P.O #</th>
                        <th className="text-center" style={{ minWidth: 160 }}>Part</th>
                        <th className="text-center" style={{ minWidth: 130 }}>Shipping Date</th>
                        <th className="text-center" style={{ minWidth: 160 }}>Order Status</th>
                        <th className="text-center" style={{ minWidth: 170 }}>Line Status</th>
                        <th className="text-center bom-availability-cell">BOM</th>
                        <th className="text-center" style={{ minWidth: 80 }}>Ship Qty</th>
                        <th className="text-center" style={{ minWidth: 70 }}>QOH</th>
                        <th className="text-center" style={{ minWidth: 70 }}>QR</th>
                        <th className="text-center" style={{ minWidth: 70 }}>QOO</th>
                        <th className="text-center" style={{ minWidth: 90 }}>Qty Avail</th>
                        <th className="text-center" style={{ minWidth: 140 }}>Shipping Method</th>
                        <th className="text-center" style={{ minWidth: 120 }}>Country</th>
                        <th className="text-center" style={{ minWidth: 220 }}>Sticky Notes</th>
                        <th className="text-center" style={{ minWidth: 80 }}>CDCR File</th>
                        <th className="text-center" style={{ minWidth: 140 }}>Order Access</th>
                    </tr>
                </thead>
                <tbody>
                    {lines.map((line) => {
                        const rowProps = getRowProps?.(line) || {};
                        const statusLine = line._headerLine || line;
                        const orderNum = line.order?.ordernum || line.ordernum;
                        const folderLink = getFolderLink?.(line) || null;
                        const isOrange = !!rowProps.isOrange;
                        const partId = line.part?.id;
                        const partLabel = formatPartNumber(line.prcpart);
                        const qoh = Number(line.part?.qoh_cache) || 0;
                        const qr = Number(line.part?.qr_cache) || 0;
                        const qoo = Number(line.part?.qoo_cache) || 0;
                        const qtyAvail = qoh - qr;
                        const countryName = line.order?.country?.country_Name || line.order?.country?.country_name || '';
                        const shipDate = line.promisedate || line.orig_shipdate || 'N/A';
                        const bomKey = String(line.id);
                        const showBom = !!bomOpen[bomKey];
                        const excludedParts = excludedPartsByOrder?.[orderNum] || excludedPartsByOrder?.[String(orderNum)] || {};
                        const lineAvailability = availabilityReportForLine(line, excludedParts);
                        const customer = line.order?.customer;
                        const woNum = line.order?.woNum;
                        return (
                            <React.Fragment key={`${orderNum}-${line.id}`}>
                                <tr className={rowProps.className} style={rowProps.style}>
                                    <td className="text-center orders-info-cell">
                                        <div className="d-flex align-items-center justify-content-center gap-2 flex-wrap">
                                            <button
                                                type="button"
                                                className={`btn btn-sm order-line-bom-btn${showBom ? ' is-open' : ''}`}
                                                aria-expanded={showBom}
                                                onClick={() => toggleBom(bomKey)}
                                            >
                                                BOM
                                            </button>
                                            <OrderHistoryIconButton
                                                unreadCount={getHistoryUnread?.(orderNum) || 0}
                                                onClick={() => onHistory?.(line)}
                                            />
                                            {renderComments?.(line)}
                                        </div>
                                    </td>
                                    <td className="text-center order-line-number">{line.lineitem ?? '-'}</td>
                                    <td className="text-center">
                                        <div className="d-flex align-items-center justify-content-center gap-2">
                                            <a
                                                href={`https://gigaflightinc.cetecerp.com/order/MN${orderNum}/view`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {getOrderLabel?.(line) || orderNum}
                                            </a>
                                            <a
                                                href={folderLink || '#'}
                                                onClick={(event) => { if (!folderLink) event.preventDefault(); }}
                                                target={folderLink ? '_blank' : undefined}
                                                rel={folderLink ? 'noopener noreferrer' : undefined}
                                                title={folderLink ? `Open folder: ${folderLink}` : 'No folder link saved yet'}
                                                style={{ cursor: folderLink ? 'pointer' : 'not-allowed' }}
                                            >
                                                <i className="fa fa-folder" aria-hidden="true" style={{ color: folderLink ? (isOrange ? '#cc7a00' : '#ff8c00') : '#cccccc' }}></i>
                                            </a>
                                        </div>
                                    </td>
                                    <td className="text-center">
                                        {customer?.id ? (
                                            <a href={`https://gigaflightinc.cetecerp.com/customer/${customer.id}/view`} target="_blank" rel="noopener noreferrer">
                                                {customer.name || ''}
                                            </a>
                                        ) : (customer?.name || '')}
                                    </td>
                                    <td className="text-center">
                                        <div className="d-flex align-items-center justify-content-center gap-2">
                                            {woNum && woNum !== '-' ? (
                                                <a href={`https://gigaflightinc.cetecerp.com/order/MN${woNum}/view`} target="_blank" rel="noopener noreferrer">
                                                    {woNum}
                                                </a>
                                            ) : '-'}
                                            {woNum && woNum !== '-' && (
                                                <a
                                                    href={line.order?.woUrl || '#'}
                                                    onClick={(event) => { if (!line.order?.woUrl) event.preventDefault(); }}
                                                    target={line.order?.woUrl ? '_blank' : undefined}
                                                    rel={line.order?.woUrl ? 'noreferrer' : undefined}
                                                    title={line.order?.woUrl ? `Open WO folder: ${line.order.woUrl}` : 'No WO folder link available'}
                                                >
                                                    <i className="fa fa-folder" aria-hidden="true" style={{ color: line.order?.woUrl ? (isOrange ? '#cc7a00' : '#ff8c00') : '#cccccc' }}></i>
                                                </a>
                                            )}
                                        </div>
                                    </td>
                                    <td className="text-center">{line.order?.custponum || ''}</td>
                                    <td className="text-center">
                                        <div className="d-inline-flex align-items-center justify-content-center gap-1">
                                            <span>{partLabel}</span>
                                            {renderPartLinkButton(`line-${line.id}`, partId)}
                                        </div>
                                    </td>
                                    <td className="text-center">{shipDate}</td>
                                    <td>{renderOrderStatus(statusLine)}</td>
                                    <td>{renderLineStatus(line)}</td>
                                    <td className="text-center bom-availability-cell">
                                        <BomAvailabilityPill missing={lineAvailability.missingPartsCount} total={lineAvailability.totalPartsCount} />
                                    </td>
                                    <td className="text-center">{formatQty(line.balancedue)}</td>
                                    <td className="text-center">{qoh}</td>
                                    <td className="text-center">{qr}</td>
                                    <td className="text-center">
                                        <div className="d-flex align-items-center justify-content-center">
                                            <span>{qoo}</span>
                                            {qoo > 0 && partId && (
                                                <a className="ms-2" href={`https://gigaflightinc.cetecerp.com/part/${partId}/pos`} target="_blank" rel="noreferrer" title="Purchase orders">
                                                    <i className="fa fa-shopping-cart text-primary" aria-hidden="true"></i>
                                                </a>
                                            )}
                                        </div>
                                    </td>
                                    <td className="text-center">
                                        <span className={qtyAvail < 0 ? 'bg-danger text-white px-2 py-1 rounded' : ''}>{qtyAvail}</span>
                                    </td>
                                    <td className="text-center">{renderShippingMethod?.(line.order?.shipmentMethod?.methodOfShipment)}</td>
                                    <td className={countryName && countryName !== 'United States' ? 'bg-dark text-light text-center country-cell-intl' : 'text-center'}>
                                        {countryName}
                                    </td>
                                    <td className="notes-column sticky-notes-column">{renderStickyNotes?.(line)}</td>
                                    <td className="text-center">
                                        <button
                                            type="button"
                                            className="btn btn-link p-0"
                                            title="Customer Drawing Change Request"
                                            disabled={!line.id}
                                            onClick={() => setCdcrLine(line)}
                                            style={{ color: '#0071b9' }}
                                        >
                                            <i className="fa-solid fa-file-pen" aria-hidden="true"></i>
                                        </button>
                                    </td>
                                    <td className="order-access-column">{renderOrderAccess(line)}</td>
                                </tr>
                                {showBom && (
                                    <tr>
                                        <td colSpan={21} className="order-line-bom-cell">
                                            <div className="order-line-bom-wrap">
                                                <div className="order-line-bom-title">BOM Parts · Line {line.lineitem ?? '-'}</div>
                                                {renderBom(line)}
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>
            {partLinkMenu && createPortal(
                <div
                    className="part-link-dropdown-menu"
                    style={{
                        position: 'fixed',
                        top: partLinkMenu.top,
                        left: partLinkMenu.left,
                        minWidth: 190,
                        zIndex: 10000,
                        backgroundColor: '#fff',
                        border: '1px solid #dee2e6',
                        borderRadius: 8,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        padding: '4px 0',
                    }}
                    onClick={(event) => event.stopPropagation()}
                >
                    <a className="dropdown-item d-flex align-items-center" href={`https://gigaflightinc.cetecerp.com/part/${partLinkMenu.partId}/inventory`} target="_blank" rel="noreferrer">
                        <i className="fa fa-boxes me-2" aria-hidden="true" style={{ color: '#28a745' }}></i>Inventory
                    </a>
                    <a className="dropdown-item d-flex align-items-center" href={`https://gigaflightinc.cetecerp.com/part/${partLinkMenu.partId}/waterfall`} target="_blank" rel="noreferrer">
                        <i className="fa fa-chart-line me-2" aria-hidden="true" style={{ color: '#ffc107' }}></i>Waterfall
                    </a>
                    <a className="dropdown-item d-flex align-items-center" href={`https://gigaflightinc.cetecerp.com/part/${partLinkMenu.partId}/pos`} target="_blank" rel="noreferrer">
                        <i className="fa fa-shopping-cart me-2" aria-hidden="true" style={{ color: '#007bff' }}></i>Purchase Orders
                    </a>
                </div>,
                document.body
            )}
            <CdcrModal
                show={!!cdcrLine}
                orderLine={cdcrLine}
                seed={{ customerName: cdcrLine?.order?.customer?.name }}
                onClose={() => setCdcrLine(null)}
            />
        </div>
    );
};

export default OrderLineView;
